import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { Switch, Title, Combobox } from '@restworld/ui-ds';

import {
  employerJobListingJpStatusFilterAtom,
  employerJobListingLocationFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
} from '../employer-job-listing.atoms';
import { useAtomValue } from 'jotai';
import useEmployerRestaurantLocations from '../../restaurants/hooks/use-employer-restaurant-locations';
import useEmployerRestaurantNames from '../../restaurants/hooks/use-employer-restaurant-names';

export function JobListingJpStatusFilter() {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultJpStatus = useAtomValue(employerJobListingJpStatusFilterAtom);

  return (
    <FiltersContainer title="">
      <Controller
        name="jpStatus"
        defaultValue={defaultJpStatus}
        control={control}
        render={({ field }) => {
          const isChecked = field.value?.includes('online') || false;
          const onChange = (checked: boolean) => {
            field.onChange(checked ? ['online'] : []);
          };
          return (
            <SwitchContainer>
              <SwitchLabel>{translateCommon('show_only_open_positions')}</SwitchLabel>
              <Switch
                checked={isChecked}
                onChange={onChange}
                srOnlyLabel={translateCommon('show_only_open_positions')}
                size="md"
              />
            </SwitchContainer>
          );
        }}
      />
    </FiltersContainer>
  );
}

export function JobListingLocationFilter({ ...rest }) {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultLocation = useAtomValue(employerJobListingLocationFilterAtom);
  const locations = useEmployerRestaurantLocations();

  return (
    <FiltersContainer title={translateCommon('location')}>
      <Controller
        name="location"
        control={control}
        defaultValue={defaultLocation}
        render={({ field }) => (
          <Combobox
            {...rest}
            value={field.value}
            onChange={(location: string) => {
              field.onChange(location);
            }}
            dropdownList={locations.data}
            placeholder={translateCommon('select_location')}
            fullwidth
          />
        )}
      />
    </FiltersContainer>
  );
}

export function JobListingRestaurantNameFilter({ ...rest }) {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultRestaurantName = useAtomValue(employerJobListingRestaurantNameFilterAtom);
  const restaurantInfo = useEmployerRestaurantNames();

  const dropdownList = useMemo(
    () => restaurantInfo?.data?.map((restaurant) => restaurant.restaurant_name),
    [restaurantInfo?.data]
  );

  return (
    <FiltersContainer title={translateCommon('restaurant_name')}>
      <Controller
        name="restaurantName"
        control={control}
        defaultValue={defaultRestaurantName}
        render={({ field }) => (
          <Combobox
            {...rest}
            value={field.value}
            onChange={(location: string) => {
              field.onChange(location);
            }}
            dropdownList={dropdownList}
            placeholder={translateCommon('select_restaurant_name')}
            fullwidth
          />
        )}
      />
    </FiltersContainer>
  );
}

const FiltersContainer = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <FilterContainer>
    <FilterTitle title={title} />
    <MainContent>{children}</MainContent>
  </FilterContainer>
);

const FilterContainer = tw.div`flex flex-col gap-4`;

const SwitchContainer = tw.div`flex items-center justify-between gap-3`;

const SwitchLabel = tw.span`text-neutral-300 font-medium text-base`;

const MainContent = tw.div``;

const FilterTitle = ({ title }: { title: string }) => (
  <Title content={title} fontWeight="medium" fontSize="base" twStyle={tw`leading-6`} />
);
